'use client';

/**
 * SelectRenderer Component
 *
 * Renders SELECT and MULTI_SELECT field types.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { getNeonStyles, type NeonAccent, neonAccents } from '../../../theme/palette';
import { glassEffects } from '../../../theme/shadows';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode, FieldOption } from '../../../types/entityFramework';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface SelectRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledFormControlProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledFormControl = styled(
  FormControl,
  transientOptions,
)<StyledFormControlProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  const sizeStyles = {
    small: { fontSize: '0.875rem' },
    medium: { fontSize: '1rem' },
    large: { fontSize: '1.125rem' },
  };

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: glass.background,
      backdropFilter: glass.backdropBlur,
      transition: transitionPresets.input,
      fontSize: sizeStyles[$size].fontSize,

      '& fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.border : glass.border,
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : glass.borderHover,
      },

      '&.Mui-focused': {
        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },
  };
});

const StyledMenuItem = styled(
  MenuItem,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const accentColor = $neonAccent ? neonAccents[$neonAccent].base : theme.palette.primary.main;

  return {
    borderRadius: 8,
    margin: '4px 8px',
    transition: transitionPresets.button,

    '&:hover': {
      backgroundColor: alpha(accentColor, $isDark ? 0.15 : 0.1),
    },

    '&.Mui-selected': {
      backgroundColor: alpha(accentColor, $isDark ? 0.25 : 0.15),
      '&:hover': {
        backgroundColor: alpha(accentColor, $isDark ? 0.3 : 0.2),
      },
    },
  };
});

const InlineFormControl = styled(
  FormControl,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },

    '& .MuiSelect-select': {
      padding: '4px 8px',
    },
  };
});

const DisplayChip = styled(
  Chip,
  transientOptions,
)<{ $color?: string; $isDark: boolean }>(({ $color, $isDark }) => ({
  backgroundColor: $color ? alpha($color, $isDark ? 0.2 : 0.15) : undefined,
  color: $color || ($isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)'),
  borderColor: $color ? alpha($color, 0.3) : undefined,
}));

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
}));

// ============================================================================
// Helpers
// ============================================================================

const findOption = (options: FieldOption[] | undefined, value: string): FieldOption | undefined => {
  return options?.find((opt) => opt.value === value);
};

const normalizeValue = (value: unknown, isMulti: boolean): string | string[] => {
  if (isMulti) {
    if (Array.isArray(value)) {
      return value.map(String);
    }
    return value ? [String(value)] : [];
  }
  return value != null ? String(value) : '';
};

// ============================================================================
// Component
// ============================================================================

export const SelectRenderer: React.FC<SelectRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const isMulti = field.fieldType === 'MULTI_SELECT';
  const normalizedValue = normalizeValue(value, isMulti);
  const options = field.options || [];

  // Handle value change
  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    onChange?.(newValue);
  };

  // Render selected chips for multi-select
  const renderValue = (selected: string | string[]) => {
    if (isMulti && Array.isArray(selected)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((val) => {
            const option = findOption(options, val);
            return (
              <DisplayChip
                key={val}
                label={option?.label || val}
                size="small"
                $color={option?.color}
                $isDark={isDark}
              />
            );
          })}
        </Box>
      );
    }
    const option = findOption(options, selected as string);
    return option?.label || selected;
  };

  // Display mode
  if (mode === 'display') {
    if (isMulti && Array.isArray(normalizedValue)) {
      if (normalizedValue.length === 0) {
        return (
          <Typography
            variant="body2"
            className={className}
            sx={{
              color: 'text.secondary',
            }}
          >
            -
          </Typography>
        );
      }
      return (
        <Box className={className} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {normalizedValue.map((val) => {
            const option = findOption(options, val);
            return (
              <DisplayChip
                key={val}
                label={option?.label || val}
                size="small"
                variant="outlined"
                $color={option?.color}
                $isDark={isDark}
              />
            );
          })}
        </Box>
      );
    }

    const selectedOption = findOption(options, normalizedValue as string);
    if (!selectedOption) {
      return (
        <Typography
          variant="body2"
          className={className}
          sx={{
            color: 'text.secondary',
          }}
        >
          -
        </Typography>
      );
    }

    if (selectedOption.color) {
      return (
        <DisplayChip
          label={selectedOption.label}
          size="small"
          variant="outlined"
          $color={selectedOption.color}
          $isDark={isDark}
          className={className}
        />
      );
    }

    return (
      <DisplayValue variant="body1" $isDark={isDark} className={className}>
        {selectedOption.label}
      </DisplayValue>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <InlineFormControl
        size="small"
        fullWidth
        error={!!error}
        disabled={disabled || field.disabled}
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
      >
        <Select
          value={normalizedValue}
          onChange={handleChange}
          multiple={isMulti}
          renderValue={isMulti ? renderValue : undefined}
          MenuProps={{
            slotProps: {
              backdrop: { invisible: true },
              paper: {
                sx: {
                  background: glass.background,
                  backdropFilter: glass.backdropBlur,
                  border: `1px solid ${glass.border}`,
                  boxShadow: glass.shadow,
                  borderRadius: 2,
                  mt: 1,
                },
              },
            },
          }}
        >
          {options.map((option) => (
            <StyledMenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              $isDark={isDark}
              $neonAccent={neonAccent}
            >
              {option.color && (
                <Box
                  component="span"
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: option.color,
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
              )}
              {option.label}
            </StyledMenuItem>
          ))}
        </Select>
      </InlineFormControl>
    );
  }

  // Edit mode
  return (
    <StyledFormControl
      fullWidth
      error={!!error}
      disabled={disabled || field.disabled}
      required={field.required}
      $isDark={isDark}
      $neonAccent={neonAccent}
      $hasError={!!error}
      $size={size}
      className={className}
    >
      {field.label && <InputLabel>{field.label}</InputLabel>}
      <Select
        value={normalizedValue}
        onChange={handleChange}
        label={field.label}
        multiple={isMulti}
        renderValue={isMulti ? renderValue : undefined}
        MenuProps={{
          slotProps: {
            backdrop: { invisible: true },
            paper: {
              sx: {
                background: glass.background,
                backdropFilter: glass.backdropBlur,
                border: `1px solid ${glass.border}`,
                boxShadow: glass.shadow,
                borderRadius: 2,
                mt: 1,
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <StyledMenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            $isDark={isDark}
            $neonAccent={neonAccent}
          >
            {option.color && (
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: option.color,
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
            )}
            {option.label}
          </StyledMenuItem>
        ))}
      </Select>
      {(error || field.helpText) && <FormHelperText error={!!error}>{error || field.helpText}</FormHelperText>}
    </StyledFormControl>
  );
};

SelectRenderer.displayName = 'SelectRenderer';

export default SelectRenderer;
